import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import { setTaskInfo } from '../store/slices/taskInfoSlice';
import { setCaseIntendedClientId } from '../store/slices/intendedInfoSlice';
import { addOrUpdateContacts } from '../store/slices/emergencyContactsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { apiFetch, reportTime } from '../utils/utils';

import SocketAccess from '../comm/SocketAccess';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import TaskAssocGroup from '../components/TaskAssocGroup';
import Charts from '../components/CrmCharts';
import KSpinner from '../components/KSpinner';

import dashboardStyle from '../assets/jss/crm/views/dashboardStyle.jsx';

const localStyles = (theme) => ({
  ...dashboardStyle(theme),
  card: {
    backgroundColor: '#e7e7e7',
    width: 'auto',
    padding: '0 10px',
    margin: '10px 10px',
  },
});

class Dashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      redirect: undefined,
      haveLoggedInUser: false,
      loading: false,
      redisplayToggle: false,
    };
  }

  componentDidMount() {
    const userId = this.props.userInfo.userId;
    const haveUserId = userId && userId.length > 10;
    const firmId = DA.getFirmId();
    const haveFirmId = firmId && firmId > 0;
    const haveLoggedInUser = haveUserId && haveFirmId;

    this.setState({ haveLoggedInUser, loading: haveLoggedInUser });

    if (haveLoggedInUser) {
      const finalCallback = () => {
        this.fetchTaskInformationAndRender();
      };
      ReduxLoader.loadReferrerInfo(this, firmId, finalCallback);
    }
    this.registerForWebSocketUpdates();
  }

  registerForWebSocketUpdates() {
    const taskCallbackStruct = {
      kind: 'task',
      pageName: 'Dashboard',
      refreshFunc: () => this.redisplayTaskLists(),
    };
    SocketAccess.registerRefreshCallback(taskCallbackStruct);
  }

  componentWillUnmount() {
    SocketAccess.unregisterRefreshCallbacksForPage('Dashboard');
  }

  redisplayTaskLists() {
    this.setState({ redisplayToggle: !this.state.redisplayToggle });
  }

  handleClick = (event, id) => {
    this.props.setCaseIntendedClientId(id);
    this.setState({ redirect: 'case' });
  };

  fetchTaskInformationAndRender() {
    const userPersonId = this.props.userInfo.personId;
    if (DA.haveTaskInfoForUser(userPersonId)) {
      this.setState({ loading: false });
      return;
    }
    const userId = this.props.userInfo.userId;
    const haveUserId = userId && userId.length > 10;
    const firmId = DA.getFirmId();
    const haveFirmId = firmId && firmId > 0;
    if (haveUserId && haveFirmId) {
      this.setState({ loading: true });
      // fetch all the task info for this user and store in Redux state
      const uri = '/api/task/infoForUser/' + userId;
      const goodResponseAction = (taskInfo) => {
        reportTime('tasks4User', b4Info4User);
        const emergencyContactsForTasks = taskInfo.emergencyContacts || [];
        emergencyContactsForTasks.forEach((ec) => {
          this.addEmergencyContact(ec);
        });
        this.props.setTaskInfo(taskInfo);
        this.fetchSubjectsOfTasks(taskInfo.tasks);
      };
      const b4Info4User = DateTime.local();
      apiFetch(this, uri, goodResponseAction, '[GET] /api/task/infoForUser/<userId>');
    } else {
      this.setState({ loading: false });
    }
  }

  addEmergencyContact(ec) {
    /*
     * ec has form:
        public int ClientContactId { get; set; }
        public int ClientPersonId { get; set; }
        public int ContactPersonId { get; set; }
        public string Relationship { get; set; }
        public int ViewingFirmFirmId { get; set; }
        public string Description { get; set; }
     */
    const { clientPersonId, contactPersonId, relationship, description } = ec;
    const triple = { personId: contactPersonId, relationship, description };
    this.props.addOrUpdateContacts({ clientId: clientPersonId, contacts: [triple] });
  }

  fetchSubjectsOfTasks(tasks) {
    const subjectIds = tasks.map((task) => task.subjectId);
    const progressCallback = null;
    const finalCallback = () => {
      this.setState({ loading: false });
    };
    ReduxLoader.loadPersonsForQueueOfPersonIds(subjectIds, progressCallback, finalCallback);
  }

  filterTasksForThoseAcceptedByUser() {
    const personId = this.props.userInfo.personId;
    const tasks = DA.getAllTasks();
    return tasks.filter((t) => t.claimantId === personId && t.currentState !== 'Canceled');
  }

  filterTasksForThoseExpectedOfUserAndNotYetAccepted() {
    const personId = this.props.userInfo.personId;
    const tasks = DA.getAllTasks();
    return tasks.filter(
      (t) =>
        t.currentState === 'Assigned' &&
        (t.assigneeId === personId || this.taskIsAssignedToGroupIncludingUser(t, personId))
    );
  }

  taskIsAssignedToGroupIncludingUser(task, personId) {
    const groupId = task.assignedGroupId;
    if (groupId === 0) {
      return false;
    }
    const group = this.props.taskInfo.taskableGroups.find((tg) => tg.id === groupId);
    return group && group.members.some((member) => member.personId === personId);
  }

  filterTasksForUnassigned() {
    const tasks = DA.getAllTasks();
    return tasks.filter((t) => t.currentState === 'Unassigned');
  }

  getPossibleTaskSubjects() {
    // the possible list is huge; consider telling the TaskAssocGroup not to display the "+"
    // or make it searchable

    let contactIds = [];

    // get all leads
    const inquiries = DA.getAllInquiries().map((i) => {
      return { personId: i.inquiryId, preferredFullName: i.name, status: 'Lead' };
    });

    // get all clients
    const clients = this.props.clientsInfo.clientIds.map((cid) => {
      const person = DA.getPersonById(cid);
      if (person) {
        return { personId: person.personId, preferredFullName: person.preferredFullName, status: person.status };
      }
    });

    // get all referral companies
    const companyIds = DA.getIdsOfFirmsVisibleToFirm(this.props.firmInfo.firmId);

    // get all referral companies
    const companies = companyIds.map((cid) => {
      const company = DA.getFirmById(cid);
      if (company) {
        if (company.contactIds) {
          contactIds = contactIds.concat(company.contactIds);
        }
        return { personId: company.firmId, preferredFullName: company.name, status: 'Referral Company' };
      }
    });

    // get all referral contact
    const contacts = contactIds.map((cid) => {
      const person = DA.getPersonById(cid);
      if (person) {
        return { personId: person.personId, preferredFullName: person.preferredFullName, status: 'Referral Contact' };
      }
    });

    const subjects = [...inquiries, ...clients, ...companies, ...contacts];

    return subjects;
  }

  getTaskSubjectCategory() {
    return 'multiple';
  }

  render() {
    if (!this.state.haveLoggedInUser) {
      return <h3>You must be logged in to view Tasks</h3>;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    const { classes } = this.props;
    const x = this.filterTasksForThoseAcceptedByUser();
    const y = this.filterTasksForThoseExpectedOfUserAndNotYetAccepted();
    const allMyTasks = x.concat(y).sort((t1, t2) => t1.nextDeadline.valueOf() - t2.nextDeadline.valueOf());
    const openTasks = this.filterTasksForUnassigned().sort(
      (t1, t2) => t1.nextDeadline.valueOf() - t2.nextDeadline.valueOf()
    );

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card className={classes.card}>
            <CardHeader color="warning" icon>
              <h3 className={classes.cardTitle}>My Tasks</h3>
            </CardHeader>
            <CardBody>
              <div className={classes.taskColumnHolder}>
                <div className={classes.taskColumn}>
                  <TaskAssocGroup taskSubjectProvider={this} allRelevantTasks={allMyTasks} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card className={classes.card}>
            <CardHeader color="warning" icon>
              <h3 className={classes.cardTitle}>Unassigned</h3>
            </CardHeader>
            <CardBody>
              <div className={classes.taskColumnHolder}>
                <div className={classes.taskColumn}>
                  <TaskAssocGroup taskSubjectProvider={this} allRelevantTasks={openTasks} />
                </div>
              </div>
            </CardBody>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Charts />
        </Grid>
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  firmInfo: state.firmInfo,
  taskInfo: state.taskInfo,
  clientsInfo: state.clientsInfo,
});
const actionProducers = { setApiResponseInfo, setTaskInfo, setCaseIntendedClientId, addOrUpdateContacts };
export default connect(mapStateToProps, actionProducers)(withStyles(localStyles)(Dashboard));
